"use client";

import RootComponentWrapper from "@components/RootWrapper";
import { getQueryClient } from "@lib/helpers/useApi";
import { initializeDatadog } from "@lib/monitoring/initializeDatadog";
import { hasWindow } from "@lib/utils/hasWindow";
import { initializeKount } from "@lib/utils/kount/session";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@ui/ThemeProvider";
import { SessionProvider } from "next-auth/react";
import { NextIntlClientProvider } from "next-intl";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import { ZustandProvider } from "../../store";

const ReactQueryDevtools = dynamic(() =>
    import("@tanstack/react-query-devtools").then((mod) => mod.ReactQueryDevtools)
);

const useInitializeServices = () => {
    useEffect(() => {
        if (hasWindow) {
            initializeKount();
            initializeDatadog();
        }
    }, []);
};

interface ClientProvidersProps {
    children: React.ReactNode;
    locale: string;
}

export default function ClientProviders({ children, locale }: ClientProvidersProps) {
    useInitializeServices();
    const queryClient = getQueryClient();

    return (
        <NextIntlClientProvider locale={locale}>
            <SessionProvider>
                <QueryClientProvider client={queryClient}>
                    <ZustandProvider>
                        <RootComponentWrapper>
                            <ThemeProvider>
                                {children}
                                <ReactQueryDevtools initialIsOpen={false} />
                            </ThemeProvider>
                        </RootComponentWrapper>
                    </ZustandProvider>
                </QueryClientProvider>
            </SessionProvider>
        </NextIntlClientProvider>
    );
}
